import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Bookmark, ChevronDown, ExternalLink, Pause, Play, Rewind, FastForward, X } from "lucide-react";
import { playerStore, usePlayer } from "@/lib/playerStore";
import UpNextPanel from "@/components/UpNextPanel";
import BookmarkMomentSheet from "@/components/BookmarkMomentSheet";
import { useAuth } from "@/contexts/AuthContext";
import { prefetchEpisode } from "@/lib/episodePrefetch";

const AUTO_EXPAND_DURATION = 3000;
const AUTO_COLLAPSE_TRANSITION = 2400;
const EASING = "cubic-bezier(0.22, 1, 0.36, 1)";

const HIDDEN_ROUTES = new Set([
  "/login",
  "/forgot-password",
  "/reset-password",
  "/staff/login",
]);



function formatTime(sec: number): string {
  if (!isFinite(sec) || sec < 0) sec = 0;
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/**
 * Persistent thin bar pinned to the top of the viewport whenever an episode
 * is loaded. Tapping the bar expands a full controls panel underneath
 * (artwork, scrub bar, skip 15/30, big play/pause, deep link to the episode
 * page). On cold-launch via the iOS Dynamic Island the user expects to land
 * on playback controls — when we rehydrate with a persisted track we
 * auto-expand once so that deep-link feels native.
 */
export default function MiniPlayerBar() {
  const current = usePlayer((s) => s.current);
  const playing = usePlayer((s) => s.playing);
  const currentTime = usePlayer((s) => s.currentTime);
  const duration = usePlayer((s) => s.duration);
  const location = useLocation();
  const navigate = useNavigate();

  const [expanded, setExpanded] = useState(false);
  const [autoCollapsing, setAutoCollapsing] = useState(false);
  const [bookmarkSheet, setBookmarkSheet] = useState<{ open: boolean; ts: number }>({ open: false, ts: 0 });
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const lastAutoExpandedKey = useRef<string | null>(null);
  const autoCollapseTimer = useRef<number | null>(null);
  const initialPathnameRef = useRef(location.pathname);
  const dragStartY = useRef(0);
  const { user } = useAuth();

  const visible = !!current && !HIDDEN_ROUTES.has(location.pathname);

  // Any manual interaction cancels the scheduled auto-collapse so the user
  // stays in control once they touch the player.
  const setExpandedSafe = useCallback((value: boolean) => {
    if (autoCollapseTimer.current) {
      window.clearTimeout(autoCollapseTimer.current);
      autoCollapseTimer.current = null;
    }
    setAutoCollapsing(false);
    setDragOffset(0);
    setIsDragging(false);
    setExpanded(value);
  }, []);

  // Swipe-up-to-dismiss: the expanded panel can be dragged upward like an
  // iPhone notification. Grabbing the panel and dragging past the threshold
  // folds it back into the collapsed bar.
  const SWIPE_CLOSE_THRESHOLD = 100;

  const isInteractiveTarget = (target: EventTarget | null) => {
    if (!(target instanceof HTMLElement)) return false;
    const interactive = target.closest("button, a, input, textarea, select, [role='slider'], [role='button']");
    return !!interactive;
  };

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    if (!expanded || autoCollapsing) return;
    if (isInteractiveTarget(e.target)) return;
    dragStartY.current = e.clientY;
    setIsDragging(true);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }, [expanded, autoCollapsing]);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging) return;
    const delta = dragStartY.current - e.clientY;
    if (delta < 0) return; // ignore downward pulls
    e.preventDefault();
    setDragOffset(delta);
  }, [isDragging]);

  const onPointerUp = useCallback((e: React.PointerEvent) => {
    if (!isDragging) return;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      // capture may already be released
    }
    const delta = dragStartY.current - e.clientY;
    if (delta >= SWIPE_CLOSE_THRESHOLD) {
      // Subtle haptic confirmation when the swipe crosses the dismissal threshold.
      try { (navigator as Navigator & { vibrate?: (pattern: number | number[]) => boolean }).vibrate?.(12); } catch { /* ignore */ }
      setAutoCollapsing(true);
      // Let the snap animation finish before hiding the panel.
      window.setTimeout(() => {
        setExpanded(false);
        setAutoCollapsing(false);
        setDragOffset(0);
        setIsDragging(false);
        // Subtle haptic confirmation once the panel has fully folded away.
        try { (navigator as Navigator & { vibrate?: (pattern: number | number[]) => boolean }).vibrate?.(8); } catch { /* ignore */ }
      }, AUTO_COLLAPSE_TRANSITION);
    } else {
      setDragOffset(0);
      setIsDragging(false);
    }
  }, [isDragging]);

  // Every time a new episode becomes current (fresh Play tap, auto-advance,
  // or cold-launch rehydrate via Dynamic Island), expand the panel so the
  // user sees the full controls, then gently slide it up after 3 seconds.
  // A ref keyed by episode.key ensures we only trigger once per track and
  // don't re-fire on unrelated re-renders.
  useEffect(() => {
    if (!current) return;
    const key = current.key;
    if (lastAutoExpandedKey.current === key) return;
    lastAutoExpandedKey.current = key;

    if (autoCollapseTimer.current) {
      window.clearTimeout(autoCollapseTimer.current);
      autoCollapseTimer.current = null;
    }
    setAutoCollapsing(false);
    setExpanded(true);

    autoCollapseTimer.current = window.setTimeout(() => {
      setAutoCollapsing(true);
      autoCollapseTimer.current = window.setTimeout(() => {
        setExpanded(false);
        setAutoCollapsing(false);
        autoCollapseTimer.current = null;
      }, AUTO_COLLAPSE_TRANSITION);
    }, AUTO_EXPAND_DURATION);
  }, [current?.key]);

  // Per-history-entry memory of whether the panel was expanded. When the
  // user hits the browser Back button we restore the previous entry's
  // expansion state so returning from an episode page feels seamless —
  // playback is global so it keeps going regardless.
  const expandedByKeyRef = useRef<Map<string, boolean>>(new Map());
  const prevLocationKeyRef = useRef<string>(location.key);

  useEffect(() => {
    const prevKey = prevLocationKeyRef.current;
    // Remember the state we are leaving so a future Back/Forward can restore it.
    expandedByKeyRef.current.set(prevKey, expanded);
    prevLocationKeyRef.current = location.key;

    if (location.pathname === initialPathnameRef.current && prevKey === location.key) return;

    const restored = expandedByKeyRef.current.get(location.key);
    if (restored === true) {
      // Returning to an entry that was previously expanded — restore it
      // without re-triggering the auto-collapse timer.
      setExpandedSafe(true);
    } else if (autoCollapseTimer.current) {
      // A fresh play just kicked off the auto-expand → auto-collapse cycle
      // for this same navigation (e.g. "Listen Now" on the daily pick).
      // Leave the panel expanded so the user still sees the brief preview
      // before it slides away.
    } else {
      setExpandedSafe(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, location.pathname]);

  // Push page content down by the bar's height. When expanded the panel is
  // a floating dropdown overlay so we keep the offset equal to the bar
  // only.
  useEffect(() => {
    if (!visible) {
      document.body.classList.remove("has-mini-player");
      return;
    }
    document.body.classList.add("has-mini-player");
    return () => document.body.classList.remove("has-mini-player");
  }, [visible]);

  const rootRef = useRef<HTMLDivElement | null>(null);

  // Close expanded panel on Escape.
  useEffect(() => {
    if (!expanded) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setExpandedSafe(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [expanded]);

  // Collapse when the user clicks/taps anywhere outside the player region.
  useEffect(() => {
    if (!expanded) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (target && rootRef.current && !rootRef.current.contains(target)) {
        setExpandedSafe(false);
      }
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, [expanded, setExpandedSafe]);


  if (!visible || !current) return null;

  const pct = duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0;

  const scrubFromClientX = (clientX: number, rect: DOMRect) => {
    if (!duration) return;
    const p = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    playerStore.seek(p * duration);
  };

  const onSlimScrub = (e: React.MouseEvent<HTMLDivElement>) => {
    scrubFromClientX(e.clientX, e.currentTarget.getBoundingClientRect());
  };

  const onFatScrubPointer = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    el.setPointerCapture(e.pointerId);
    const rect = el.getBoundingClientRect();
    scrubFromClientX(e.clientX, rect);
    const move = (ev: PointerEvent) => scrubFromClientX(ev.clientX, rect);
    const up = (ev: PointerEvent) => {
      el.releasePointerCapture(ev.pointerId);
      el.removeEventListener("pointermove", move);
      el.removeEventListener("pointerup", up);
      el.removeEventListener("pointercancel", up);
    };
    el.addEventListener("pointermove", move);
    el.addEventListener("pointerup", up);
    el.addEventListener("pointercancel", up);
  };

  const togglePlay = () => {
    // Per spec: tapping play/pause from the collapsed bar also opens the panel.
    if (!expanded) setExpandedSafe(true);
    playerStore.toggle();
  };

  const skip = (delta: number) => {
    const next = Math.max(0, Math.min(duration || 0, currentTime + delta));
    playerStore.seek(next);
  };

  const openEpisode = () => {
    if (current.episodeSlug) {
      prefetchEpisode(current.episodeSlug);
      // Preserve the current entry's expansion state so Back restores it.
      // The navigation effect collapses the *new* entry by default.
      navigate(`/episode/${current.episodeSlug}`);
    }
  };

  const warmEpisode = () => prefetchEpisode(current.episodeSlug);

  return (
    <div
      ref={rootRef}
      className="fixed top-0 left-0 right-0 z-[60] pointer-events-none"
      role="region"
      aria-label="Now playing"
    >
      <div className="pointer-events-auto bg-background border-b-2 border-foreground shadow-[0_3px_0_0_hsl(var(--foreground)/0.15)]">
        <div
          className="container px-3 sm:px-4 flex items-center gap-2 sm:gap-3 h-10 sm:h-11 cursor-pointer"
          onClick={() => setExpandedSafe(!expanded)}
          role="button"
          aria-label={expanded ? "Collapse player" : "Expand player"}
        >
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); togglePlay(); }}
          aria-label={playing ? "Pause" : "Play"}
          title={playing ? "Pause" : "Play"}
          className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 border-2 border-foreground rounded-md bg-[hsl(195,85%,55%)] text-white flex items-center justify-center shadow-[2px_2px_0_0_hsl(var(--foreground))] active:translate-x-[1px] active:translate-y-[1px] active:shadow-[1px_1px_0_0_hsl(var(--foreground))] transition-transform"
        >
          {playing ? (
            <Pause className="w-3.5 h-3.5" strokeWidth={3} />
          ) : (
            <Play className="w-3.5 h-3.5 ml-[1px]" strokeWidth={3} fill="currentColor" />
          )}
        </button>

        <div
          onPointerEnter={warmEpisode}
          onPointerDown={warmEpisode}
          className="flex-1 min-w-0 text-left"
          title={expanded ? "Tap to collapse" : "Tap to expand"}
        >
          <div className="flex items-baseline gap-1.5 min-w-0">
            <span className="font-mono font-bold text-[11px] sm:text-xs truncate">
              {current.title}
            </span>
            {current.podcastName && (
              <span className="hidden sm:inline font-mono text-[10px] text-muted-foreground truncate">
                · {current.podcastName}
              </span>
            )}
          </div>
        </div>

        <span
          aria-hidden="true"
          className="flex-shrink-0 p-1.5 -m-1 text-muted-foreground"
        >
          <ChevronDown
            className={`w-4 h-4 transition-transform ${expanded ? "rotate-180" : ""}`}
            strokeWidth={2.5}
            style={{ transitionTimingFunction: EASING }}
          />
        </span>

        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setExpandedSafe(false); playerStore.stop(); }}
          aria-label="Close player"
          title="Stop and close player"
          className="flex-shrink-0 p-1.5 -m-1 text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="w-4 h-4" strokeWidth={2.5} />
        </button>
      </div>

      {/* Slim progress bar — visible when collapsed, clickable to seek. */}
      {!expanded && (
        <div
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={duration || 0}
          aria-valuenow={currentTime}
          onClick={onSlimScrub}
          className="h-[3px] w-full bg-border cursor-pointer"
          title="Tap to scrub"
        >
          <div
            className="h-full bg-accent"
            style={{ width: `${pct}%`, transition: `width ${AUTO_COLLAPSE_TRANSITION}ms ${EASING}` }}
          />
        </div>
      )}
      </div>

      {expanded && (
        <div
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          className="pointer-events-auto bg-transparent overflow-visible pt-2 sm:pt-0 will-change-[transform,opacity]"
          style={{
            transform: autoCollapsing
              ? "translateY(-100%)"
              : isDragging
              ? `translateY(-${dragOffset}px)`
              : "translateY(0)",
            opacity: autoCollapsing ? 0 : 1,
            transition: isDragging
              ? "none"
              : `transform ${AUTO_COLLAPSE_TRANSITION}ms ${EASING}, opacity ${AUTO_COLLAPSE_TRANSITION}ms ${EASING}`,
          }}
        >
          {/* Expanded player as a floating card with the same neo-brutalist
              shadow as episode cards. Mobile: inset from the edges with a
              thick border + 6px shadow. Desktop: anchored under the bar. */}
          <div className="px-2 sm:pl-4 sm:pr-0 sm:pt-3">
            <div className="mx-auto sm:mx-0 w-full sm:w-[380px] sm:max-w-[380px] bg-card border-[3px] border-foreground rounded-lg shadow-[6px_6px_0px_0px_hsl(var(--foreground))]" style={{ touchAction: "none" }}>
              {/* Visual drag handle — panel can be swiped up anywhere to dismiss. */}
              <div className="pt-2 pb-1 flex justify-center pointer-events-none" aria-hidden="true">
                <div className="w-8 h-1 rounded-full bg-muted-foreground/40" />
              </div>
              <div className="px-3 sm:px-4 py-3 sm:py-4 flex flex-col gap-3">
            <div className="flex items-center gap-3">
              {current.artworkUrl ? (
                <img
                  src={current.artworkUrl}
                  alt=""
                  className="w-12 h-12 sm:w-14 sm:h-14 rounded-md border-2 border-foreground shadow-[2px_2px_0_0_hsl(var(--foreground))] flex-shrink-0"
                />
              ) : null}
              <div className="min-w-0 flex-1">
                <div className="font-mono font-bold text-sm truncate">{current.title}</div>
                {current.podcastName && (
                  <div className="font-mono text-[11px] text-muted-foreground truncate">
                    {current.podcastName}
                  </div>
                )}
              </div>
              {current.episodeSlug && (
                <button
                  type="button"
                  onClick={openEpisode}
                  onPointerEnter={warmEpisode}
                  onPointerDown={warmEpisode}
                  onFocus={warmEpisode}
                  aria-label="Open episode page"
                  title="Open episode page"
                  className="flex-shrink-0 p-2 -m-1 border-2 border-foreground rounded-md bg-card shadow-[2px_2px_0_0_hsl(var(--foreground))] active:translate-x-[1px] active:translate-y-[1px] active:shadow-[1px_1px_0_0_hsl(var(--foreground))] transition-transform"
                >
                  <ExternalLink className="w-4 h-4" strokeWidth={2.5} />
                </button>
              )}
            </div>

            {/* Fat scrub bar — drag or tap. */}
            <div className="flex flex-col gap-1">
              <div
                role="slider"
                aria-label="Seek"
                aria-valuemin={0}
                aria-valuemax={duration || 0}
                aria-valuenow={currentTime}
                tabIndex={0}
                onPointerDown={onFatScrubPointer}
                className="relative h-3 w-full bg-border border-2 border-foreground rounded-full cursor-pointer touch-none select-none"
                title="Drag or tap to scrub"
              >
                <div
                  className="absolute inset-y-0 left-0 bg-accent rounded-full"
                  style={{ width: `${pct}%`, transition: `width ${AUTO_COLLAPSE_TRANSITION}ms ${EASING}` }}
                />
                <div
                  className="absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-background border-2 border-foreground shadow-[1px_1px_0_0_hsl(var(--foreground))]"
                  style={{ left: `calc(${pct}% - 8px)`, transition: `left ${AUTO_COLLAPSE_TRANSITION}ms ${EASING}` }}
                />
              </div>
              <div className="flex justify-between font-mono text-[10px] text-muted-foreground">
                <span>{formatTime(currentTime)}</span>
                <span>-{formatTime(Math.max(0, (duration || 0) - currentTime))}</span>
              </div>
            </div>

            <div className="flex items-center justify-center gap-4">
              <button
                type="button"
                onClick={() => skip(-15)}
                aria-label="Back 15 seconds"
                title="Back 15s"
                style={{ ["--btn-comic-tilt" as string]: "-2deg" }}
                className="btn-comic btn-comic-secondary !p-0 w-11 h-11 flex flex-col items-center justify-center gap-0.5"
              >
                <Rewind className="w-5 h-5" strokeWidth={2.5} />
                <span className="text-[10px] font-mono font-bold leading-none">15</span>
              </button>

              <button
                type="button"
                onClick={() => playerStore.toggle()}
                aria-label={playing ? "Pause" : "Play"}
                title={playing ? "Pause" : "Play"}
                style={{ ["--btn-comic-tilt" as string]: "-3deg" }}
                className="btn-comic !rounded-full !p-0 w-14 h-14 bg-[hsl(195,85%,55%)] text-white border-foreground"
              >
                {playing ? (
                  <Pause className="w-6 h-6" strokeWidth={3} />
                ) : (
                  <Play className="w-6 h-6 ml-[2px]" strokeWidth={3} fill="currentColor" />
                )}
              </button>

              <button
                type="button"
                onClick={() => skip(30)}
                aria-label="Forward 30 seconds"
                title="Forward 30s"
                style={{ ["--btn-comic-tilt" as string]: "2deg" }}
                className="btn-comic btn-comic-secondary !p-0 w-11 h-11 flex flex-col items-center justify-center gap-0.5"
              >
                <FastForward className="w-5 h-5" strokeWidth={2.5} />
                <span className="text-[10px] font-mono font-bold leading-none">30</span>
              </button>
            </div>

            {user && current.episodeId ? (
              <button
                type="button"
                onClick={() => {
                  const ts = Math.floor(currentTime);
                  try { (navigator as Navigator & { vibrate?: (n: number) => boolean }).vibrate?.(15); } catch { /* ignore */ }
                  setBookmarkSheet({ open: true, ts });
                }}
                aria-label="Bookmark this moment"
                title="Bookmark this moment"
                className="btn-comic-primary w-full flex items-center justify-center gap-2"
                style={{ minHeight: 56 }}
              >
                <Bookmark className="w-5 h-5" strokeWidth={2.5} fill="currentColor" />
                <span>Bookmark this moment at {formatTime(currentTime)}</span>
              </button>
            ) : null}

            <UpNextPanel />

              </div>
            </div>
          </div>
        </div>
      )}

      {user && current.episodeId ? (
        <BookmarkMomentSheet
          open={bookmarkSheet.open}
          onClose={() => setBookmarkSheet((s) => ({ ...s, open: false }))}
          episodeId={current.episodeId}
          episodeTitle={current.title}
          podcastName={current.podcastName ?? undefined}
          artworkUrl={current.artworkUrl ?? undefined}
          timestampSeconds={bookmarkSheet.ts}
        />
      ) : null}
    </div>
  );
}
